import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Combobox } from '../ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Package, Pencil, StopCircle, Plus } from 'lucide-react';

export const TabComodato = ({
  comodatoAlocacoes,
  comodatoItens,
  internacao,
  showComodatoModal,
  setShowComodatoModal,
  comodatoForm,
  setComodatoForm,
  handleAddComodato,
  handleRemoveComodato,
  handleUpdateComodatoQtd
}) => (
  <>
    <TabsContent value="comodato">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Comodato (equipamentos emprestados)
              </CardTitle>
              <CardDescription>
                Equipamentos e materiais em comodato para esta internacao
              </CardDescription>
            </div>
            {internacao?.status_kanban !== 'alta_efetivada' && (
              <Button onClick={() => { setComodatoForm({ comodato_item_id: '', quantidade_em_uso: 1, observacoes: '' }); setShowComodatoModal(true); }} data-testid="btn-adicionar-comodato-perfil">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar item
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {comodatoAlocacoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum equipamento em comodato</p>
              <p className="text-sm">Adicione itens emprestados para este paciente</p>
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Item</th>
                    <th className="text-center p-3 font-medium">Quantidade</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {comodatoAlocacoes.map(aloc => (
                    <tr key={aloc.id} className="border-t" data-testid={`comodato-aloc-row-${aloc.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-500" />
                          <span className="font-medium">{aloc.item_nome || 'Item desconhecido'}</span>
                        </div>
                        {aloc.observacoes && (
                          <p className="text-xs text-muted-foreground mt-1">{aloc.observacoes}</p>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {aloc.quantidade_em_uso}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={aloc.status === 'ativo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}>
                          {aloc.status === 'ativo' ? 'Ativo' : 'Encerrado'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center gap-1">
                          {aloc.status === 'ativo' && internacao?.status_kanban !== 'alta_efetivada' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const novaQtd = prompt('Nova quantidade:', aloc.quantidade_em_uso);
                                  if (novaQtd && parseInt(novaQtd) > 0) {
                                    handleUpdateComodatoQtd(aloc.id, parseInt(novaQtd));
                                  }
                                }}
                                title="Alterar quantidade"
                                data-testid={`btn-edit-comodato-aloc-${aloc.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveComodato(aloc.id)}
                                title="Encerrar alocacao"
                                data-testid={`btn-remove-comodato-aloc-${aloc.id}`}
                              >
                                <StopCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    {/* Comodato Modal */}
    <Dialog open={showComodatoModal} onOpenChange={setShowComodatoModal}>
      <DialogContent data-testid="dialog-comodato-alocacao">
        <DialogHeader>
          <DialogTitle>Adicionar Item de Comodato</DialogTitle>
          <DialogDescription>
            Selecione um equipamento para emprestar a este paciente
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Item de comodato *</Label>
            {(() => {
              const options = comodatoItens
                .filter(i => i.disponiveis > 0)
                .map(item => ({
                  value: item.id,
                  label: `${item.nome} (${item.disponiveis} disponivel)`,
                  subtitle: item.categoria || undefined
                }));
              return (
                <Combobox
                  options={options}
                  value={comodatoForm.comodato_item_id}
                  onValueChange={(v) => setComodatoForm(prev => ({ ...prev, comodato_item_id: v }))}
                  placeholder="Buscar item de comodato..."
                  emptyMessage="Nenhum item disponivel"
                  data-testid="select-comodato-item"
                />
              );
            })()}
          </div>
          <div className="space-y-2">
            <Label>Quantidade *</Label>
            <Input
              type="number"
              min="1"
              max={(() => {
                const item = comodatoItens.find(i => i.id === comodatoForm.comodato_item_id);
                return item ? item.disponiveis : 999;
              })()}
              value={comodatoForm.quantidade_em_uso}
              onChange={e => setComodatoForm(prev => ({ ...prev, quantidade_em_uso: parseInt(e.target.value) || 1 }))}
              data-testid="input-comodato-aloc-qtd"
            />
            {comodatoForm.comodato_item_id && (
              <p className="text-xs text-muted-foreground">
                Disponivel: {comodatoItens.find(i => i.id === comodatoForm.comodato_item_id)?.disponiveis || 0}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Observacoes</Label>
            <Input
              value={comodatoForm.observacoes || ''}
              onChange={e => setComodatoForm(prev => ({ ...prev, observacoes: e.target.value }))}
              placeholder="Observacoes opcionais..."
              data-testid="input-comodato-aloc-obs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowComodatoModal(false)}>Cancelar</Button>
          <Button
            onClick={handleAddComodato}
            disabled={!comodatoForm.comodato_item_id || comodatoForm.quantidade_em_uso < 1}
            data-testid="btn-salvar-comodato-aloc"
          >
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);
